import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Truck, CheckCircle, MapPin, ArrowRight, ChevronRight, Home, Clock, Shield, Phone, Navigation } from 'lucide-react';
import { Button } from '../components/ui/Button';
interface InterstateDeliveryPageProps {
  onNavigate?: (page: string) => void;
}
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12
    }
  }
};
const nigerianStates = ['Abuja (FCT)', 'Kano', 'Rivers', 'Oyo', 'Kaduna', 'Ogun', 'Anambra', 'Imo', 'Delta', 'Edo', 'Enugu', 'Katsina', 'Borno', 'Ondo', 'Osun'];
export function InterstateDeliveryPage({
  onNavigate
}: InterstateDeliveryPageProps) {
  return <div className="flex flex-col bg-white" data-id="element-2163">
      {/* Hero Section */}
      <section className="relative min-h-[520px] lg:min-h-[600px] text-white overflow-hidden -mt-20 sm:-mt-[104px] pt-20 sm:pt-[104px]" data-id="element-2164">
        <div className="absolute inset-0" data-id="element-2165">
          <img src="https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" alt="Delivery truck on highway" className="absolute inset-0 w-full h-full object-cover" data-id="element-2166" />
          <div className="absolute inset-0 bg-gray-900/65" data-id="element-2167" />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-gray-900/50 to-gray-900/30" data-id="element-2168" />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-transparent to-gray-900/30" data-id="element-2169" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20 sm:pt-20 sm:pb-20 lg:pt-28 lg:pb-28" data-id="element-2170">
          <nav className="flex items-center gap-2 text-sm text-white/70 mb-8" data-id="element-2171">
            <button onClick={() => onNavigate?.('home')} className="flex items-center gap-1 hover:text-white transition-colors" data-id="element-2172">
              <Home className="w-4 h-4" data-id="element-2173" />
              <span data-id="element-2174">Home</span>
            </button>
            <ChevronRight className="w-4 h-4" data-id="element-2175" />
            <span className="text-white/50" data-id="element-2176">Services</span>
            <ChevronRight className="w-4 h-4" data-id="element-2177" />
            <span className="text-white font-medium" data-id="element-2178">Interstate Delivery</span>
          </nav>

          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="max-w-3xl" data-id="element-2179">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-black/40 border border-white/30 text-white text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-2180">
              <Truck className="w-4 h-4" data-id="element-2181" />
              Add-on Service
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 leading-tight" style={{
            textShadow: '0 2px 4px rgba(0,0,0,0.5), 0 4px 12px rgba(0,0,0,0.4)'
          }} data-id="element-2182">
              Interstate Delivery{' '}
              <span className="text-brand-300" data-id="element-2183">Across Nigeria</span>
            </h1>
            <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-2xl leading-relaxed" style={{
            textShadow: '0 1px 3px rgba(0,0,0,0.5)'
          }} data-id="element-2184">
              Your package arrives in Lagos — then we take it further. Add
              interstate delivery to reach any state in Nigeria, right to your
              recipient's door.
            </p>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-2185">
              <Button size="lg" className="bg-brand-500 hover:bg-brand-600 text-white border-none shadow-lg px-8" onClick={() => onNavigate?.('shipping')} data-id="element-2186">
                Add to My Shipment <ArrowRight className="ml-2 w-5 h-5" data-id="element-2187" />
              </Button>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10 px-8" onClick={() => onNavigate?.('contact')} data-id="element-2188">
                Ask a Question
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How it works with Lagos */}
      <section className="py-20 lg:py-24 bg-white" data-id="element-2189">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2190">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-2191">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-2192">
              <span className="inline-block px-4 py-1.5 bg-brand/10 text-brand text-sm font-semibold rounded-full mb-6" data-id="element-2193">
                How It Works
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6" data-id="element-2194">
                Beyond Lagos, Anywhere in Nigeria
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-2195">
                Our standard service delivers to Lagos. With the interstate
                add-on, we coordinate ground transport from our Lagos hub to any
                Nigerian state — so your recipient doesn't have to travel to
                pick up their package.
              </p>
              <div className="space-y-5" data-id="element-2196">
                <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl" data-id="element-2197">
                  <div className="w-10 h-10 bg-brand-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-2198">
                    <MapPin className="w-5 h-5 text-brand-600" data-id="element-2199" />
                  </div>
                  <div data-id="element-2200">
                    <div className="font-semibold text-gray-900 mb-1" data-id="element-2201">
                      Step 1: Arrives in Lagos
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-2202">
                      Your package clears customs and arrives at our Lagos hub.
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl" data-id="element-2203">
                  <div className="w-10 h-10 bg-brand-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-2204">
                    <Truck className="w-5 h-5 text-brand-600" data-id="element-2205" />
                  </div>
                  <div data-id="element-2206">
                    <div className="font-semibold text-gray-900 mb-1" data-id="element-2207">
                      Step 2: Ground Transport Dispatched
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-2208">
                      We coordinate with trusted local carriers to move your
                      package to the destination state.
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl" data-id="element-2209">
                  <div className="w-10 h-10 bg-brand-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-2210">
                    <CheckCircle className="w-5 h-5 text-brand-600" data-id="element-2211" />
                  </div>
                  <div data-id="element-2212">
                    <div className="font-semibold text-gray-900 mb-1" data-id="element-2213">
                      Step 3: Door Delivery
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-2214">
                      Your recipient receives the package at their address — no
                      pickup required.
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-2215">
              <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200" data-id="element-2216">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-2217">
                  States We Cover
                </h3>
                <p className="text-sm text-gray-500 mb-6" data-id="element-2218">
                  We deliver to all 36 states + FCT. Here are some popular
                  destinations:
                </p>
                <div className="grid grid-cols-2 gap-2" data-id="element-2219">
                  {nigerianStates.map(state => <div key={state} className="flex items-center gap-2 py-1.5" data-id="element-2220">
                      <CheckCircle className="w-4 h-4 text-brand-500 flex-shrink-0" data-id="element-2221" />
                      <span className="text-sm text-gray-700" data-id="element-2222">{state}</span>
                    </div>)}
                  <div className="flex items-center gap-2 py-1.5 col-span-2" data-id="element-2223">
                    <CheckCircle className="w-4 h-4 text-brand-500 flex-shrink-0" data-id="element-2224" />
                    <span className="text-sm text-gray-700 font-medium" data-id="element-2225">
                      + All remaining states
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 lg:py-24 bg-gray-50" data-id="element-2226">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2227">
          <div className="text-center mb-14" data-id="element-2228">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-id="element-2229">
              Why Add Interstate Delivery?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-2230">
              Skip the hassle of arranging your own transport from Lagos.
            </p>
          </div>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2231">
            <motion.div variants={fadeIn} className="flex flex-col items-center text-center" data-id="element-2232">
              <div className="w-16 h-16 bg-brand-50 rounded-2xl flex items-center justify-center mb-5" data-id="element-2233">
                <Navigation className="w-8 h-8 text-brand-600" data-id="element-2234" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2235">
                Nationwide Reach
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-2236">
                Deliver to all 36 states and the FCT. No destination is too far.
              </p>
            </motion.div>
            <motion.div variants={fadeIn} className="flex flex-col items-center text-center" data-id="element-2237">
              <div className="w-16 h-16 bg-brand-50 rounded-2xl flex items-center justify-center mb-5" data-id="element-2238">
                <Clock className="w-8 h-8 text-brand-600" data-id="element-2239" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2240">
                Fast Turnaround
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-2241">
                Most interstate deliveries complete within 2–5 business days
                after Lagos arrival.
              </p>
            </motion.div>
            <motion.div variants={fadeIn} className="flex flex-col items-center text-center" data-id="element-2242">
              <div className="w-16 h-16 bg-brand-50 rounded-2xl flex items-center justify-center mb-5" data-id="element-2243">
                <Shield className="w-8 h-8 text-brand-600" data-id="element-2244" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2245">
                Trusted Partners
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-2246">
                We work with vetted local carriers to ensure safe, reliable
                delivery across Nigeria.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-24 bg-white" data-id="element-2247">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2248">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-gradient-to-br from-brand-600 to-brand-700 rounded-3xl p-10 sm:p-16 shadow-xl" data-id="element-2249">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-id="element-2250">
              Ship anywhere in Nigeria
            </h2>
            <p className="text-xl text-brand-100 mb-8 max-w-xl mx-auto" data-id="element-2251">
              Add interstate delivery when you create your shipment — it's that
              simple.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2252">
              <Button variant="white" size="lg" className="px-10" onClick={() => onNavigate?.('shipping')} data-id="element-2253">
                Start Shipping Now
              </Button>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10 px-10" onClick={() => onNavigate?.('contact')} data-id="element-2254">
                Contact Us
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}